<script setup lang="ts">
import { ref, watch } from 'vue';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

const props = withDefaults(
  defineProps<{
    modelValue: string | null | undefined;
    placeholder?: string;
    disabled?: boolean;
    /** denser toolbar useful for email compose */
    variant?: "default" | "email";
  }>(),
  { variant: "default" }
);

const emit = defineEmits(['update:modelValue']);

const internalValue = ref(props.modelValue || '');

watch(() => props.modelValue, (newVal) => {
  if (newVal !== internalValue.value) {
    internalValue.value = newVal || '';
  }
});

const onUpdate = () => {
  emit('update:modelValue', internalValue.value);
};

const toolbarOptions =
  props.variant === "email"
    ? [
        ["bold", "italic", "underline"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ header: [1, 2, false] }],
        ["blockquote", "link"],
        ["clean"],
      ]
    : [
        ["bold", "italic"],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "clean"],
      ];
</script>

<template>
  <div class="quill-editor-wrapper" :class="{ 'is-disabled': disabled, 'is-email': variant === 'email' }">
    <QuillEditor
      v-model:content="internalValue"
      contentType="html"
      :placeholder="placeholder"
      :toolbar="toolbarOptions"
      theme="snow"
      :readOnly="disabled"
      @update:content="onUpdate"
    />
  </div>
</template>

<style scoped>
.quill-editor-wrapper :deep(.ql-editor) {
  min-height: 150px;
  max-height: 400px;
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.5;
}

.quill-editor-wrapper.is-email :deep(.ql-editor) {
  min-height: 220px;
  max-height: 360px;
}

.quill-editor-wrapper :deep(.ql-toolbar.ql-snow) {
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  border-color: rgb(226, 232, 240);
}

.quill-editor-wrapper :deep(.ql-container.ql-snow) {
  border-bottom-left-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
  border-color: rgb(226, 232, 240);
}

.dark .quill-editor-wrapper :deep(.ql-toolbar.ql-snow),
.dark .quill-editor-wrapper :deep(.ql-container.ql-snow) {
  border-color: rgb(71, 85, 105);
  background-color: transparent;
}

.dark .quill-editor-wrapper :deep(.ql-editor) {
  color: rgb(226, 232, 240);
}

.dark .quill-editor-wrapper :deep(.ql-stroke) {
  stroke: rgb(226, 232, 240);
}

.dark .quill-editor-wrapper :deep(.ql-fill) {
  fill: rgb(226, 232, 240);
}

.dark .quill-editor-wrapper :deep(.ql-picker) {
  color: rgb(226, 232, 240);
}

.is-disabled :deep(.ql-toolbar) {
  display: none;
}

.is-disabled :deep(.ql-container) {
  border-radius: 0.375rem;
}
</style>
